import type { Rng } from '../core/rng.js';

export type BotDifficulty = 'BEGINNER' | 'EASY' | 'NORMAL' | 'HARD' | 'EXPERT';
export const BOT_DIFFICULTIES: readonly BotDifficulty[] = ['BEGINNER', 'EASY', 'NORMAL', 'HARD', 'EXPERT'];

/**
 * Tunables shared by all strategies. Each game strategy interprets them in its own way, but the
 * meaning is consistent: higher difficulty = fewer mistakes, more memory, more computation.
 */
export interface DifficultyProfile {
  /** Probability of deliberately choosing a sub-optimal (but legal) action. */
  mistakeRate: number;
  /** 0..1 — how much of the public history the bot remembers (card counting). */
  memory: number;
  /** Monte-Carlo samples / simulations used by search-based strategies. */
  simulations: number;
  /** 0..1 — willingness to bet/raise with marginal hands. */
  aggression: number;
  /** 0..1 — probability of bluffing in betting games. */
  bluff: number;
  /** Human-like think time range for the platform scheduler. */
  thinkMs: [number, number];
}

export const DIFFICULTY_PROFILES: Record<BotDifficulty, DifficultyProfile> = {
  BEGINNER: { mistakeRate: 0.35, memory: 0, simulations: 0, aggression: 0.2, bluff: 0.02, thinkMs: [900, 2600] },
  EASY: { mistakeRate: 0.2, memory: 0.3, simulations: 0, aggression: 0.35, bluff: 0.05, thinkMs: [800, 2300] },
  NORMAL: { mistakeRate: 0.08, memory: 0.7, simulations: 60, aggression: 0.5, bluff: 0.1, thinkMs: [700, 2000] },
  HARD: { mistakeRate: 0.03, memory: 1, simulations: 150, aggression: 0.6, bluff: 0.14, thinkMs: [600, 1800] },
  EXPERT: { mistakeRate: 0, memory: 1, simulations: 300, aggression: 0.65, bluff: 0.18, thinkMs: [500, 1600] },
};

export interface BotContext {
  seat: number;
  difficulty: BotDifficulty;
  profile: DifficultyProfile;
  rng: Rng;
}

export function makeBotContext(seat: number, difficulty: BotDifficulty, rng: Rng): BotContext {
  return { seat, difficulty, profile: DIFFICULTY_PROFILES[difficulty], rng };
}

/**
 * Common bot contract (spec §7). Bots receive ONLY the redacted view for their own seat plus the
 * legal-action description — exactly what a human client receives — so they cannot cheat, and
 * every action they return is validated by the same engine code path as human actions.
 */
export interface BotEngine<V, L, A> {
  /** Main entry point: pick an action. */
  decideAction(view: V, legal: L, ctx: BotContext): A;
  /** Estimated strength of the bot's position in [0, 1] (win probability / equity). */
  evaluateState(view: V, ctx: BotContext): number;
  /** Risk of the most aggressive available option in [0, 1]. */
  calculateRisk(view: V, legal: L, ctx: BotContext): number;
  /** Fallback used when the bot itself times out or throws — must always be legal and cheap. */
  handleTimeout(view: V, legal: L, ctx: BotContext): A;
  /** Trick/meld games. */
  chooseCard?(view: V, legal: L, ctx: BotContext): A;
  /** Betting games and Call Break bidding. */
  chooseBet?(view: V, legal: L, ctx: BotContext): A;
  /** Betting games: true if the bot should fold. */
  chooseFold?(view: V, legal: L, ctx: BotContext): boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyBotEngine = BotEngine<any, any, any>;

export function chance(ctx: BotContext, p: number): boolean {
  return ctx.rng.next() < p;
}
